/**
 * Quantity-based bundle discount (e.g. 2 items => ₹999, 3 items => ₹1499).
 * Tiers are defined server-side in bundle-discount-server/config.php.
 *
 * Whenever the cart changes, asks the PHP backend whether the cart's total
 * quantity matches a bundle tier. If it does, the backend returns a
 * one-time Shopify discount code that brings the order to that tier's
 * price, and this script applies it directly to the cart (via
 * /cart/update.js's `discount` param) so cart.total_price updates
 * immediately. If the cart no longer matches any tier (e.g. it grew from
 * 2 items to 3, or dropped below 2), any previously applied code is
 * explicitly cleared from the cart — otherwise Shopify would keep
 * discounting just the original entitled line items forever.
 *
 * IMPORTANT: cart section refreshes are done WITHOUT calling Dawn's
 * cart-drawer `renderContents()`, because that method's last step force
 * opens the drawer. Calling it from a background cart sync (e.g. on every
 * page load, or after an unrelated quantity edit on the cart page) was
 * popping the drawer open unexpectedly. refreshCartDrawerSections() below
 * updates the same DOM the same way, minus the forced open.
 *
 * IMPORTANT: this theme's own `cart-update` pub/sub event is only published
 * from the cart page's quantity controls (assets/cart.js) — NOT from
 * add-to-cart (assets/product-form.js just calls cart.renderContents()
 * directly). Relying on that pub/sub alone meant adding a 2nd product from
 * a product page never re-evaluated the bundle until the next full page
 * load. Instead, we watch the network directly for any POST to
 * /cart/add, /cart/change or /cart/update and re-evaluate after each one,
 * so it doesn't matter which theme component (product form, quick-add
 * modal, cart drawer, cart page) triggered the mutation.
 */

// Set this to the PHP endpoint from bundle-discount-server/index.php
const BUNDLE_DISCOUNT_API_URL = 'https://int.thecartninja.com/houseofko/index.php';

const STORAGE_KEY = 'bundleDiscount';
const CART_MUTATION_URL_PATTERN = /\/cart\/(add|change|update)(\.js)?(\?|$)/;

// The discount code currently applied to the live Shopify cart, as far as
// this page load knows. `undefined` = not yet checked this page load, so
// the first apply/clear call always runs even if it looks like a no-op.
let appliedCode;

// True only for the synchronous duration of our own /cart/update.js call
// below, so the fetch watcher doesn't re-trigger evaluateCart() for a
// mutation we caused ourselves.
let suppressAutoEvaluate = false;

function bundleCartSignature(cart) {
  return cart.items
    .map((item) => `${item.variant_id}:${item.quantity}`)
    .sort()
    .join(',');
}

function readStoredDiscount() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
  } catch (e) {
    return null;
  }
}

function storeDiscount(signature, code, expiresAt) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ signature, code, expiresAt }));
}

function clearStoredDiscount() {
  sessionStorage.removeItem(STORAGE_KEY);
}

function isValid(stored, signature) {
  return Boolean(
    stored && stored.signature === signature && stored.expiresAt && new Date(stored.expiresAt) > new Date()
  );
}

// Holds/releases the checkout buttons using the theme's own existing
// disabled-button styling (the same one Dawn uses for an empty cart), so
// the customer can't proceed to checkout while a qualifying discount is
// still being applied to the cart.
function setCheckoutButtonsBusy(isBusy) {
  document.querySelectorAll('#checkout, #CartDrawer-Checkout').forEach((button) => {
    button.disabled = isBusy;
  });
}

function refreshCartDrawerSections(cartDrawerEl, state) {
  if (!cartDrawerEl || !state.sections || typeof cartDrawerEl.getSectionsToRender !== 'function') {
    return;
  }

  cartDrawerEl.getSectionsToRender().forEach((section) => {
    const sectionElement = section.selector ? document.querySelector(section.selector) : document.getElementById(section.id);
    const html = state.sections[section.id];
    if (sectionElement && html && typeof cartDrawerEl.getSectionInnerHTML === 'function') {
      sectionElement.innerHTML = cartDrawerEl.getSectionInnerHTML(html, section.selector);
    }
  });
  // Deliberately not calling cartDrawerEl.open() here — this refresh should
  // be invisible unless the customer already has the drawer open.
}

function setCartDiscount(code) {
  const desired = code || '';
  if (desired === appliedCode) {
    return Promise.resolve();
  }

  const cartDrawerEl = document.querySelector('cart-drawer');
  const sections = new Set(['cart-icon-bubble']);
  if (cartDrawerEl && typeof cartDrawerEl.getSectionsToRender === 'function') {
    cartDrawerEl.getSectionsToRender().forEach((section) => sections.add(section.id));
  }

  suppressAutoEvaluate = true;
  const request = fetch(window.routes.cart_update_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({
      discount: desired,
      sections: Array.from(sections),
      sections_url: window.location.pathname,
    }),
  });
  suppressAutoEvaluate = false;

  return request
    .then((response) => response.json())
    .then((state) => {
      appliedCode = desired;

      const cartPath = new URL(window.routes.cart_url, window.location.origin).pathname;
      if (window.location.pathname.replace(/\/$/, '') === cartPath.replace(/\/$/, '')) {
        window.location.reload();
        return state;
      }
      refreshCartDrawerSections(cartDrawerEl, state);
      return state;
    });
}

function requestBundleDiscount(cart, hadStoredDiscount) {
  const signature = bundleCartSignature(cart);
  const stored = readStoredDiscount();

  if (isValid(stored, signature)) {
    if (cart.total_discount > 0) {
      // Shopify's cart session already reflects this discount (e.g. it was
      // applied just before a previous reload) — nothing left to do, and
      // importantly nothing to re-POST, or we'd reload the cart page forever.
      appliedCode = stored.code;
      setCheckoutButtonsBusy(false);
      return;
    }

    setCheckoutButtonsBusy(true);
    setCartDiscount(stored.code).finally(() => setCheckoutButtonsBusy(false));
    return;
  }

  setCheckoutButtonsBusy(true);
  fetch(BUNDLE_DISCOUNT_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      items: cart.items.map((item) => ({
        variant_id: item.variant_id,
        quantity: item.quantity,
      })),
    }),
  })
    .then((response) => response.json())
    .then((data) => {
      if (data.success) {
        storeDiscount(signature, data.code, data.expires_at);
        return setCartDiscount(data.code);
      }

      clearStoredDiscount();
      // Only bother clearing on the live cart if we might actually have
      // one applied — avoids a pointless request on every normal page
      // load for carts that never triggered a bundle discount.
      if (hadStoredDiscount) {
        return setCartDiscount('');
      }
    })
    .catch(() => {
      clearStoredDiscount();
    })
    .finally(() => {
      setCheckoutButtonsBusy(false);
    });
}

function evaluateCart() {
  const hadStoredDiscount = Boolean(readStoredDiscount());

  fetch(`${window.routes.cart_url}.js`)
    .then((response) => response.json())
    .then((cart) => {
      if (cart.item_count === 0) {
        clearStoredDiscount();
        setCheckoutButtonsBusy(false);
        if (hadStoredDiscount) {
          setCartDiscount('');
        }
        return;
      }
      requestBundleDiscount(cart, hadStoredDiscount);
    })
    .catch(() => {
      setCheckoutButtonsBusy(false);
    });
}

(function watchCartMutations() {
  const originalFetch = window.fetch.bind(window);
  window.fetch = function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const shouldWatch = CART_MUTATION_URL_PATTERN.test(url) && !suppressAutoEvaluate;
    const request = originalFetch(input, init);
    if (shouldWatch) {
      request.then(() => evaluateCart()).catch(() => {});
    }
    return request;
  };
})();

document.addEventListener('DOMContentLoaded', () => {
  evaluateCart();

  // Kept as a harmless extra signal alongside the fetch watcher above.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, () => evaluateCart());
  }
});
